// ---------------------------------------------------------------------------
// Local PCM recorder for voice dictation: the webapp's JuisciGPT composer (its
// DEFAULT path) and the website's question field.
//
// Captures the mic through an AudioWorklet, downsamples to 16kHz mono 16-bit
// PCM, and on `finish()` wraps the take in a WAV container for the api's
// transcribe-dictation endpoints (Gemini 3.5 Transcribe, pre-recorded). Nothing
// streams while recording — the transcript arrives in one piece after stop,
// which is why both fields show a strong "recording" visual instead of live
// text. The webapp falls back to an OpenAI WebRTC transport
// (`dictationSession.ts`) on runtimes without AudioWorklet; the website shows
// no mic there.
//
// MediaRecorder is deliberately NOT used: its output codecs (webm/opus on
// Chrome/Android, mp4/AAC on iOS) are not in Gemini's supported audio input
// formats, while raw WAV universally is — and PCM capture keeps one code path
// across every runtime instead of a per-browser codec roulette.
// ---------------------------------------------------------------------------

const TARGET_SAMPLE_RATE = 16_000;

// A recorded take shorter than this (~0.3s of 16kHz mono PCM) is a tap-tap with
// nothing said — skip the transcription round-trip entirely.
export const MIN_TAKE_BYTES = 9_600;

// AudioWorklet module, inlined as a Blob URL so no separate build artifact is
// needed: forwards each 128-frame render quantum of the mono mic input to the
// main thread, which downsamples and buffers (keeps the worklet trivial). Any
// inbound message is answered with "flushed" — the port is FIFO per direction,
// so once that echo arrives on the main thread, every audio chunk posted
// before it has been delivered (the finish() drain below relies on this).
const CAPTURE_WORKLET_MODULE = `
registerProcessor(
  "dictation-capture",
  class extends AudioWorkletProcessor {
    constructor() {
      super();
      this.port.onmessage = () => {
        this.port.postMessage("flushed");
      };
    }
    process(inputs) {
      const channel = inputs[0] === undefined ? undefined : inputs[0][0];
      if (channel !== undefined) this.port.postMessage(channel.slice(0));
      return true;
    }
  }
);
`;

// A dead pipeline can't echo the flush; don't hang the stop on it — nothing
// more is coming, whatever was collected is the take.
const FLUSH_TIMEOUT_MS = 1_000;

/**
 * Whether `requestMicStream` failed because the user (or the browser's
 * settings) refused the mic, as opposed to no mic or a busy one.
 */
export function isMicPermissionDenied(error: unknown): boolean {
  return error instanceof DOMException && error.name === "NotAllowedError";
}

/** Prompt for + acquire the microphone. Rejects if the user denies permission. */
export async function requestMicStream(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  });
}

// Whether the runtime can run the recorder (mic capture is checked separately
// by each caller — this is only the capture/encode surface).
export function isRecordedDictationSupported(): boolean {
  return (
    typeof AudioContext !== "undefined" &&
    typeof AudioWorkletNode !== "undefined"
  );
}

// Linear-interpolation downsampler from the AudioContext's real rate (usually
// 44.1/48kHz — the context is created at the device rate on purpose, older
// WebViews reject a MediaStream source in a context at a different rate) down
// to 16kHz. Stateful: carries the fractional read position across chunks so
// long takes don't drift.
function createDownsampler(
  sourceRate: number
): (chunk: Float32Array) => Float32Array {
  if (sourceRate === TARGET_SAMPLE_RATE) return (chunk) => chunk;
  const ratio = sourceRate / TARGET_SAMPLE_RATE;
  let carry = new Float32Array(0);
  let position = 0;
  return (chunk) => {
    const source = new Float32Array(carry.length + chunk.length);
    source.set(carry, 0);
    source.set(chunk, carry.length);
    const out: number[] = [];
    while (position + 1 < source.length) {
      const base = Math.floor(position);
      const frac = position - base;
      const a = source[base];
      const b = source[base + 1];
      if (a === undefined || b === undefined) {
        throw new Error("Invariant: downsampler read past its buffer");
      }
      out.push(a + (b - a) * frac);
      position += ratio;
    }
    const consumed = Math.floor(position);
    carry = source.slice(consumed);
    position -= consumed;
    return Float32Array.from(out);
  };
}

// 16-bit little-endian PCM bytes, as the WAV data section wants (explicit
// little-endian rather than trusting Int16Array platform endianness).
function pcm16LeBytes(samples: Float32Array): Uint8Array<ArrayBuffer> {
  const buffer = new ArrayBuffer(samples.length * 2);
  const view = new DataView(buffer);
  let offset = 0;
  for (const sample of samples) {
    const clamped = Math.max(-1, Math.min(1, sample));
    view.setInt16(offset, Math.round(clamped * 0x7fff), true);
    offset += 2;
  }
  return new Uint8Array(buffer);
}

/**
 * Wrap raw 16kHz mono 16-bit PCM chunks in a canonical 44-byte WAV header.
 * Exported for its unit test — pure byte math that must not silently drift.
 */
/**
 * The part of each 16 kHz chunk a take of `maxSeconds` still keeps: whole
 * chunks up to the bound, then the head of the one that crosses it, then none.
 */
export function createTakeBound(
  maxSeconds: number
): (chunk: Float32Array) => Float32Array {
  const maxSamples = Math.floor(maxSeconds * TARGET_SAMPLE_RATE);
  let kept = 0;
  return (chunk) => {
    const part = chunk.subarray(0, Math.max(0, maxSamples - kept));
    kept += part.length;
    return part;
  };
}

export function buildWavBlob(
  pcmChunks: ReadonlyArray<Uint8Array<ArrayBuffer>>
): Blob {
  const dataBytes = pcmChunks.reduce((total, chunk) => total + chunk.length, 0);
  const header = new ArrayBuffer(44);
  const view = new DataView(header);
  const writeAscii = (offset: number, text: string): void => {
    for (let i = 0; i < text.length; i++) {
      const code = text.codePointAt(i);
      if (code === undefined) {
        throw new Error("Invariant: index inside the chunk tag");
      }
      view.setUint8(offset + i, code);
    }
  };
  writeAscii(0, "RIFF");
  view.setUint32(4, 36 + dataBytes, true);
  writeAscii(8, "WAVE");
  writeAscii(12, "fmt ");
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, TARGET_SAMPLE_RATE, true);
  view.setUint32(28, TARGET_SAMPLE_RATE * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  writeAscii(36, "data");
  view.setUint32(40, dataBytes, true);
  return new Blob([header, ...pcmChunks], { type: "audio/wav" });
}

// A live recording take. Both calls are terminal and idempotent.
export type DictationRecorderHandle = {
  /**
   * Stop capturing and get the take as a 16kHz mono WAV. Async on purpose: the
   * worklet posts audio through a message queue, so the tail of the dictation
   * may still be undelivered at the moment of the stop tap — finish drains it
   * (flush handshake) before building the WAV.
   */
  finish: () => Promise<Blob>;
  /** Stop capturing and discard the take. */
  abort: () => void;
};

/**
 * Start capturing `stream` into an in-memory PCM take. Resolves once audio is
 * flowing (the mic → worklet pipeline is up); rejects if the pipeline can't be
 * built (the webapp then falls back to its OpenAI realtime transport).
 */
export async function startDictationRecording(
  stream: MediaStream,
  // The longest take kept, whatever runs late around the stop: the audio past
  // it is dropped. Null where the caller bounds the take itself.
  maxSeconds: number | null
): Promise<DictationRecorderHandle> {
  const bound = maxSeconds === null ? null : createTakeBound(maxSeconds);
  const context = new AudioContext();
  const workletUrl = URL.createObjectURL(
    new Blob([CAPTURE_WORKLET_MODULE], { type: "application/javascript" })
  );
  let node: AudioWorkletNode | null = null;
  let source: MediaStreamAudioSourceNode | null = null;
  const pcmChunks: Uint8Array<ArrayBuffer>[] = [];
  let torn = false;
  let flushResolve: (() => void) | null = null;
  const downsample = createDownsampler(context.sampleRate);
  const onMessage = (event: MessageEvent) => {
    const data: unknown = event.data;
    // The finish() drain: everything posted before this echo has arrived.
    if (data === "flushed") {
      if (flushResolve !== null) flushResolve();
      return;
    }
    // Structured-clone boundary: the worklet only ever posts Float32Array
    // render quanta, so anything else means the co-located module drifted —
    // fail loudly rather than degrade into a truncated take.
    if (!(data instanceof Float32Array)) {
      throw new Error(
        "should not happen: dictation worklet posted a non-Float32Array"
      );
    }
    const resampled = downsample(data);
    const kept = bound === null ? resampled : bound(resampled);
    if (kept.length > 0) pcmChunks.push(pcm16LeBytes(kept));
  };

  const teardown = (): void => {
    if (torn) return;
    torn = true;
    if (node !== null) {
      node.port.removeEventListener("message", onMessage);
      node.disconnect();
      node = null;
    }
    if (source !== null) {
      source.disconnect();
      source = null;
    }
    void context.close().catch(() => undefined);
    URL.revokeObjectURL(workletUrl);
  };

  try {
    await context.audioWorklet.addModule(workletUrl);
    node = new AudioWorkletNode(context, "dictation-capture", {
      numberOfInputs: 1,
      numberOfOutputs: 0,
    });
    node.port.addEventListener("message", onMessage);
    // A listener added this way, unlike `onmessage`, needs the port started.
    node.port.start();
    source = context.createMediaStreamSource(stream);
    source.connect(node);
    // Contexts can start suspended pending a user gesture; dictation always
    // begins from a tap, so resume() succeeds.
    if (context.state === "suspended") await context.resume();
  } catch (error) {
    teardown();
    throw error instanceof Error ? error : new Error(String(error));
  }

  return {
    finish: async () => {
      const activeNode = node;
      if (!torn && activeNode !== null) {
        // Stop producing FIRST (no new quanta after the flush ping)…
        if (source !== null) source.disconnect();
        activeNode.disconnect();
        // …then drain the queue: audio already posted but not yet delivered is
        // the TAIL of the dictation — building the WAV without it truncates
        // the user's last words.
        await new Promise<void>((resolve) => {
          const timer = setTimeout(resolve, FLUSH_TIMEOUT_MS);
          flushResolve = () => {
            clearTimeout(timer);
            resolve();
          };
          activeNode.port.postMessage("flush");
        });
        flushResolve = null;
      }
      teardown();
      return buildWavBlob(pcmChunks);
    },
    abort: teardown,
  };
}
